import React, { useState, useEffect } from 'react';
import { Search, FileText, Calendar, Clock, Plus, Edit2, Trash2, Eye, Printer, BookOpen, GraduationCap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../api';
import Pagination from '../common/Pagination';
import EmptyState from '../common/EmptyState';
import Modal from '../Modal';
import FormInput from '../FormInput';
import ConfirmationModal from '../ConfirmationModal';

const EpreuvesTab = () => {
  const [epreuves, setEpreuves] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [matieres, setMatieres] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState(null);

  // États pour les modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedEpreuve, setSelectedEpreuve] = useState(null);
  const [detailEpreuve, setDetailEpreuve] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    matiere: '',
    date_epreuve: '',
    heure_debut: '',
    duree_minutes: '',
    note_sur: '20',
    coefficient: '1',
    salle: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // États pour la confirmation de suppression
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [epreuveToDelete, setEpreuveToDelete] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({});

  const navigate = useNavigate();

  // Charger les sessions disponibles
  const fetchSessions = async () => {
    try {
      const response = await api.get('/concours/sessions/');
      if (response.data) {
        const sessionData = response.data.data || response.data.results || [];
        setSessions(Array.isArray(sessionData) ? sessionData : []);
        // Sélectionner la première session par défaut
        if (sessionData.length > 0) {
          setSelectedSession(sessionData[0].id);
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des sessions:', error);
    }
  };

  // Charger les matières disponibles
  const fetchMatieres = async () => {
    try {
      const response = await api.get('/concours/matieres/');
      if (response.data) {
        const matiereData = response.data.data || response.data.results || [];
        setMatieres(Array.isArray(matiereData) ? matiereData : []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des matières:', error);
    }
  };

  const fetchEpreuves = async () => {
    if (!selectedSession) return;
    
    setLoading(true);
    setError('');
    
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        page_size: pageSize
      });
      
      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }
      
      const response = await api.get(`/concours/sessions/${selectedSession}/epreuves/?${queryParams}`);
      
      if (response.data) {
        const responseData = response.data.data || response.data.results || [];
        setEpreuves(Array.isArray(responseData) ? responseData : []);
        setPagination(response.data.pagination || {
          count: response.data.count || 0,
          total_pages: Math.ceil((response.data.count || 0) / pageSize),
          current_page: currentPage,
          page_size: pageSize
        });
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des épreuves');
      setEpreuves([]);
      setPagination(null);
      toast.error('Impossible de charger les épreuves');
    } finally {
      setLoading(false);
    }
  };

  // Charger les sessions et matières au montage
  useEffect(() => {
    fetchSessions();
    fetchMatieres();
  }, []);

  // Charger les épreuves quand la session ou les paramètres changent
  useEffect(() => {
    if (selectedSession) {
      const timeoutId = setTimeout(() => {
        fetchEpreuves();
      }, searchTerm ? 300 : 0);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedSession, currentPage, pageSize, searchTerm]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSessionChange = (e) => {
    setSelectedSession(e.target.value);
    setCurrentPage(1);
  };

  // Gestion du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear l'erreur si l'utilisateur corrige le champ
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Ouvrir le modal de création/modification
  const handleOpenModal = (epreuve = null) => {
    if (epreuve) {
      // Séparer date_heure en date et heure
      let dateEpreuve = '';
      let heureDebut = '';
      if (epreuve.date_heure) {
        const dateTime = new Date(epreuve.date_heure);
        dateEpreuve = dateTime.toISOString().split('T')[0];
        heureDebut = dateTime.toTimeString().split(' ')[0].substring(0, 5);
      }
      
      setFormData({
        nom: epreuve.nom || '',
        description: epreuve.description || '',
        matiere: epreuve.matiere?.id || '',
        date_epreuve: dateEpreuve,
        heure_debut: heureDebut,
        duree_minutes: epreuve.duree_minutes || '',
        note_sur: epreuve.note_max || '20',
        coefficient: epreuve.coefficient || '1',
        salle: epreuve.salle || ''
      });
    } else {
      setSelectedEpreuve(null);
      setFormData({
        nom: '',
        description: '',
        matiere: '',
        date_epreuve: '',
        heure_debut: '',
        duree_minutes: '',
        note_sur: '20',
        coefficient: '1',
        salle: ''
      });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  // Ouvrir le modal de détails
  const handleOpenDetailModal = (epreuve) => {
    setDetailEpreuve(epreuve);
    setIsDetailModalOpen(true);
  };

  // Fonction d'impression
  const handlePrint = () => {
    window.print();
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = {};
    
    if (!formData.nom.trim()) {
      errors.nom = "Le nom est requis";
    }
    if (!formData.matiere) {
      errors.matiere = "La matière est requise";
    }
    if (!formData.date_epreuve) {
      errors.date_epreuve = "La date est requise";
    }
    if (!formData.heure_debut) {
      errors.heure_debut = "L'heure de début est requise";
    }
    if (!formData.duree_minutes) {
      errors.duree_minutes = "La durée est requise";
    } else if (isNaN(formData.duree_minutes) || formData.duree_minutes <= 0) {
      errors.duree_minutes = "La durée doit être un nombre positif";
    }
    if (!formData.note_sur) {
      errors.note_sur = "La note sur est requise";
    } else if (isNaN(formData.note_sur) || formData.note_sur <= 0) {
      errors.note_sur = "La note sur doit être un nombre positif";
    }
    if (!formData.coefficient) {
      errors.coefficient = "Le coefficient est requis";
    } else if (isNaN(formData.coefficient) || formData.coefficient <= 0) {
      errors.coefficient = "Le coefficient doit être un nombre positif";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      // Combiner date et heure en datetime
      const dateHeure = `${formData.date_epreuve}T${formData.heure_debut}:00`;
      
      const dataToSend = {
        nom: formData.nom,
        description: formData.description,
        matiere: formData.matiere,
        date_heure: dateHeure,
        duree_minutes: parseInt(formData.duree_minutes),
        note_max: parseFloat(formData.note_sur),
        coefficient: parseFloat(formData.coefficient),
        salle: formData.salle || 'À définir'
      };

      if (selectedEpreuve) {
        // Mise à jour
        await api.put(`/concours/sessions/${selectedSession}/epreuves/${selectedEpreuve.id}/`, dataToSend);
        toast.success("Épreuve mise à jour avec succès");
      } else {
        // Création
        await api.post(`/concours/sessions/${selectedSession}/epreuves/`, dataToSend);
        toast.success("Épreuve créée avec succès");
      }

      fetchEpreuves();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Erreur lors de l'opération:", error);
      
      let errorMsg = "Une erreur est survenue. Veuillez réessayer.";
      if (error.response && error.response.data) {
        const responseData = error.response.data;
        if (typeof responseData === 'object') {
          const errorMessages = Object.entries(responseData)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('\n');
          errorMsg = errorMessages || errorMsg;
        } else if (typeof responseData === 'string') {
          errorMsg = responseData;
        }
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Gestion de la suppression
  const handleOpenDeleteModal = (epreuve) => {
    setEpreuveToDelete(epreuve);
    setConfirmConfig({
      title: "Supprimer l'épreuve",
      message: `Êtes-vous sûr de vouloir supprimer l'épreuve "${epreuve.nom}" ?`,
      confirmText: "Supprimer",
      type: "danger"
    });
    setIsConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!epreuveToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/concours/sessions/${selectedSession}/epreuves/${epreuveToDelete.id}/`);
      toast.success("Épreuve supprimée avec succès");
      fetchEpreuves();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast.error("Impossible de supprimer cette épreuve. Veuillez réessayer.");
    } finally {
      setDeleteLoading(false);
      setIsConfirmModalOpen(false);
    }
  };

  // Fonction pour naviguer vers la gestion des notes
  const handleManageNotes = (epreuve) => {
    navigate(`/concours/epreuves/${epreuve.id}/notes`);
  };

  // Rendering du footer du modal de création/modification
  const renderModalFooter = () => (
    <div className="flex justify-end space-x-4">
      <button
        type="button"
        className="inline-flex justify-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={() => setIsModalOpen(false)}
      >
        Annuler
      </button>
      <button
        type="button"
        className={`inline-flex justify-center px-6 py-2.5 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md shadow-sm hover:bg-[#161c3f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] ${submitting ? 'opacity-75 cursor-not-allowed' : ''}`}
        onClick={handleSubmit}
        disabled={submitting}
      >
        {submitting ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Traitement...
          </>
        ) : (
          selectedEpreuve ? 'Mettre à jour' : 'Créer'
        )}
      </button>
    </div>
  );

  // Rendering du footer du modal de détails
  const renderDetailModalFooter = () => (
    <div className="flex justify-between">
      <button
        type="button"
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={handlePrint}
      >
        <Printer className="h-4 w-4 mr-2" />
        Imprimer
      </button>
      <button
        type="button"
        className="inline-flex justify-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={() => setIsDetailModalOpen(false)}
      >
        Fermer
      </button>
    </div>
  );

  if (loading && epreuves.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex gap-4 flex-1">
          {/* Sélecteur de session */}
          <div className="min-w-48">
            <select
              value={selectedSession}
              onChange={handleSessionChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Sélectionner une session</option>
              {sessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {session.nom}
                </option>
              ))}
            </select>
          </div>
          
          {/* Barre de recherche */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Rechercher une épreuve..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        
        {selectedSession && (
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
          >
            <Plus className="h-5 w-5 mr-2" />
            Nouvelle Épreuve
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {!selectedSession ? (
        <EmptyState
          icon={FileText}
          title="Sélectionner une session"
          description="Veuillez sélectionner une session pour voir les épreuves."
        />
      ) : epreuves.length === 0 && !loading ? (
        <EmptyState
          icon={FileText}
          title="Aucune épreuve"
          description="Aucune épreuve trouvée pour cette session."
          actionLabel="Créer une épreuve"
          onAction={() => handleOpenModal()}
        />
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Épreuve
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Matière
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durée
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {epreuves.map((epreuve) => (
                  <tr key={epreuve.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-orange-100 flex items-center justify-center">
                            <FileText className="h-5 w-5 text-orange-600" />
                          </div>
                        </div>
                        <div className="ml-4 min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">{epreuve.nom || 'Épreuve sans nom'}</div>
                          {epreuve.description && (
                            <div className="text-sm text-gray-500 truncate">{epreuve.description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{epreuve.matiere?.nom || 'Non définie'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                        {epreuve.date_heure ? new Date(epreuve.date_heure).toLocaleDateString('fr-FR') : 'Non définie'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Clock className="h-4 w-4 mr-1 text-gray-400" />
                        {epreuve.duree_minutes || epreuve.matiere?.duree_minutes || 0} min
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleManageNotes(epreuve)}
                          className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-50 transition-colors"
                          title="Gérer les notes"
                        >
                          <GraduationCap className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleOpenDetailModal(epreuve)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50 transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleOpenModal(epreuve)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded-full hover:bg-indigo-50 transition-colors"
                          title="Modifier"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleOpenDeleteModal(epreuve)}
                          className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total_pages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200">
              <Pagination
                currentPage={currentPage}
                totalPages={pagination.total_pages}
                pageSize={pageSize}
                totalCount={pagination.count}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </div>
      )}

      {/* Modal de création/modification */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedEpreuve ? "Modifier l'épreuve" : "Nouvelle épreuve"}
        footer={renderModalFooter()}
        maxWidth="max-w-3xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInput
              label="Nom de l'épreuve"
              type="text"
              name="nom"
              value={formData.nom}
              onChange={handleInputChange}
              placeholder="Ex: Épreuve de Mathématiques"
              required
              error={formErrors.nom}
              icon={FileText}
            />
            
            <FormInput
              label="Matière"
              type="select"
              name="matiere"
              value={formData.matiere}
              onChange={handleInputChange}
              required
              error={formErrors.matiere}
              icon={BookOpen}
              options={[
                { value: '', label: 'Sélectionner une matière' },
                ...matieres.map(matiere => ({
                  value: matiere.id,
                  label: `${matiere.nom} (${matiere.code})`
                }))
              ]}
            />

            <FormInput
              label="Date de l'épreuve"
              type="date"
              name="date_epreuve"
              value={formData.date_epreuve}
              onChange={handleInputChange}
              required
              error={formErrors.date_epreuve}
              icon={Calendar}
            />

            <FormInput
              label="Heure de début"
              type="time"
              name="heure_debut"
              value={formData.heure_debut}
              onChange={handleInputChange}
              required
              error={formErrors.heure_debut}
              icon={Clock}
            />

            <FormInput
              label="Durée (minutes)"
              type="number"
              name="duree_minutes"
              value={formData.duree_minutes}
              onChange={handleInputChange}
              placeholder="Ex: 120"
              required
              error={formErrors.duree_minutes}
              min="30"
              max="360"
              step="15"
              icon={Clock}
            />

            <FormInput
              label="Note sur"
              type="number"
              name="note_sur"
              value={formData.note_sur}
              onChange={handleInputChange}
              placeholder="Ex: 20"
              required
              error={formErrors.note_sur}
              min="1"
              max="100"
              step="0.5"
            />

            <FormInput
              label="Coefficient"
              type="number"
              name="coefficient"
              value={formData.coefficient}
              onChange={handleInputChange}
              placeholder="Ex: 1"
              required
              error={formErrors.coefficient}
              min="0.5"
              max="10"
              step="0.5"
            />

            <FormInput
              label="Salle"
              type="text"
              name="salle"
              value={formData.salle}
              onChange={handleInputChange}
              placeholder="Ex: Salle A1"
              error={formErrors.salle}
            />
          </div>

          <FormInput
            label="Description"
            type="textarea"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Description de l'épreuve..."
            error={formErrors.description}
            rows={3}
          />

          <div className="text-sm text-gray-500 mt-4">
            <span className="text-red-500">*</span> Champs obligatoires
          </div>
        </form>
      </Modal>

      {/* Modal de détails */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title="Détails de l'épreuve"
        footer={renderDetailModalFooter()}
        maxWidth="max-w-3xl"
      >
        {detailEpreuve && (
          <div className="space-y-6 print:space-y-4">
            {/* En-tête pour l'impression */}
            <div className="hidden print:block text-center mb-8">
              <h1 className="text-2xl font-bold text-gray-900">Détails de l'Épreuve</h1>
              <p className="text-gray-600 mt-2">Généré le {new Date().toLocaleDateString('fr-FR')}</p>
            </div>

            {/* Informations générales */}
            <div className="bg-gray-50 p-6 rounded-lg print:bg-white print:border print:border-gray-300">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2 text-orange-600" />
                Informations générales
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nom de l'épreuve</label>
                  <p className="mt-1 text-sm text-gray-900">{detailEpreuve.nom}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Matière</label>
                  <p className="mt-1 text-sm text-gray-900">{detailEpreuve.matiere?.nom || 'Non définie'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date</label>
                  <p className="mt-1 text-sm text-gray-900 flex items-center">
                    <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                    {detailEpreuve.date_heure ? new Date(detailEpreuve.date_heure).toLocaleDateString('fr-FR') : 'Non définie'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Heure de début</label>
                  <p className="mt-1 text-sm text-gray-900 flex items-center">
                    <Clock className="h-4 w-4 mr-1 text-gray-400" />
                    {detailEpreuve.date_heure ? new Date(detailEpreuve.date_heure).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : 'Non définie'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Durée</label>
                  <p className="mt-1 text-sm text-gray-900 flex items-center">
                    <Clock className="h-4 w-4 mr-1 text-gray-400" />
                    {detailEpreuve.duree_minutes} minutes
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Note sur</label>
                  <p className="mt-1 text-sm text-gray-900">{detailEpreuve.note_sur || 20}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Coefficient</label>
                  <p className="mt-1 text-sm text-gray-900">{detailEpreuve.coefficient || 1}</p>
                </div>
              </div>
            </div>

            {/* Description */}
            {detailEpreuve.description && (
              <div className="bg-blue-50 p-6 rounded-lg print:bg-white print:border print:border-gray-300">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Description</h3>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{detailEpreuve.description}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal 
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleDelete}
        title={confirmConfig.title || "Supprimer l'épreuve"}
        message={confirmConfig.message || "Êtes-vous sûr de vouloir supprimer cette épreuve ?"}
        confirmText={confirmConfig.confirmText || "Supprimer"}
        cancelText="Annuler"
        type={confirmConfig.type || "danger"}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default EpreuvesTab; 